/**
 * Stationsliste (P6 Block C) — mit der Z14-Aufräum-Sicht.
 *
 * Real existieren zwei Geräte; die übrigen Zeilen sind Test-Ruinen und alte
 * Doppel-Kopplungen. Die Liste macht das sichtbar (zuletzt gesehen +
 * Verwendungs-Hinweis), statt es zu verstecken — die Bereinigung selbst
 * passiert beim Cutover (Z14). Neue Terminals entstehen über „Gerät koppeln",
 * nicht über einen Anlegen-Knopf.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../App';
import { LeitstandRahmen } from '../../components/leitstand/LeitstandRahmen';
import {
  Karte,
  Kennung,
  Kopfzelle,
  Notiz,
  Tabelle,
  Zelle,
  Zustand,
} from '../../components/leitstand';

interface Station {
  id: string;
  name?: string | null;
  type: string;
  location?: string | null;
  isActive: boolean;
  lastSeenAt?: string | null;
  updatedAt: string;
}

const TYP_KURZ: Record<string, string> = {
  WSA: 'WSA',
  VORARBEITER: 'Vorarbeiter',
  ADMIN: 'Vorschau (Alt)',
};

const FRISCH_TAGE = 14;

function zuletzt(iso?: string | null): { text: string; frisch: boolean } {
  if (!iso) return { text: 'nie', frisch: false };
  const d = new Date(iso);
  const tage = Math.floor((Date.now() - d.getTime()) / 86400000);
  return {
    text: d.toLocaleDateString('de-DE', {
      timeZone: 'Europe/Berlin',
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
    }),
    frisch: tage <= FRISCH_TAGE,
  };
}

/** Einstellungs-Karte: automatische Abmeldung an den Stationen (P6 Block E). */
function AbmeldeZeit() {
  const [wert, setWert] = useState('');
  const [gespeichert, setGespeichert] = useState<number | null>(null);
  const [sende, setSende] = useState(false);
  const [meldung, setMeldung] = useState<{ art: 'gut' | 'fehler'; text: string } | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const res = await fetch('/api/admin/station-config', { credentials: 'include' });
        if (!res.ok) return;
        const daten = (await res.json()) as { unlockTtlSeconds: number };
        setGespeichert(daten.unlockTtlSeconds);
        setWert(String(daten.unlockTtlSeconds));
      } catch {
        /* Karte bleibt ohne Vorbelegung nutzbar */
      }
    })();
  }, []);

  const speichere = async () => {
    setSende(true);
    setMeldung(null);
    try {
      const res = await fetch('/api/admin/station-config', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ unlockTtlSeconds: Number(wert) }),
      });
      const antwort = await res.json().catch(() => null);
      if (!res.ok) {
        throw new Error(
          antwort?.code === 'UNGUELTIGE_ABMELDEZEIT'
            ? 'Bitte einen Wert zwischen 60 und 1800 Sekunden wählen.'
            : antwort?.error || 'Speichern fehlgeschlagen',
        );
      }
      setGespeichert(antwort.unlockTtlSeconds);
      setWert(String(antwort.unlockTtlSeconds));
      setMeldung({ art: 'gut', text: '✓ Gespeichert' });
      setTimeout(() => setMeldung(null), 2500);
    } catch (e) {
      setMeldung({
        art: 'fehler',
        text: e instanceof Error ? e.message : 'Speichern fehlgeschlagen',
      });
    } finally {
      setSende(false);
    }
  };

  const geaendert = gespeichert !== null && wert !== String(gespeichert);

  return (
    <Karte titel="Automatische Abmeldung" marke="gilt für alle Stationen">
      <div className="flex flex-col gap-3">
        <p className="text-sm text-tinte-2">
          Ohne Aktivität wird die angemeldete Person nach dieser Zeit automatisch abgemeldet — jede
          Berührung an der Station verlängert sie wieder.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <input
            value={wert}
            onChange={(e) => setWert(e.target.value.replace(/[^0-9]/g, ''))}
            inputMode="numeric"
            className="w-24 rounded-[4px] border border-linie-2 bg-flaeche-2 px-3 py-2 text-right font-zahl text-sm text-tinte focus:border-akzent focus:outline-none"
          />
          <span className="text-sm text-tinte-3">Sekunden</span>
          <button
            onClick={() => void speichere()}
            disabled={sende || !geaendert}
            className="rounded-[4px] border border-akzent bg-akzent px-3 py-1.5 font-zahl text-xs text-akzent-satz disabled:opacity-40"
          >
            Speichern
          </button>
          {meldung && (
            <span className={`text-[13px] ${meldung.art === 'gut' ? 'text-lauf' : 'text-stoer'}`}>
              {meldung.text}
            </span>
          )}
        </div>
      </div>
    </Karte>
  );
}

export default function StationenListe() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [stationen, setStationen] = useState<Station[]>([]);
  const [laden, setLaden] = useState(true);
  const [fehler, setFehler] = useState('');

  const lade = useCallback(async () => {
    setLaden(true);
    setFehler('');
    try {
      const res = await fetch('/api/einrichten/objekt/stationen', { credentials: 'include' });
      if (!res.ok) throw new Error('Die Stationsliste konnte nicht geladen werden');
      setStationen(((await res.json()).objekte ?? []) as Station[]);
    } catch (e) {
      setFehler(e instanceof Error ? e.message : 'Die Stationsliste konnte nicht geladen werden');
    } finally {
      setLaden(false);
    }
  }, []);

  useEffect(() => {
    void lade();
  }, [lade]);

  const sortiert = [...stationen].sort((a, b) =>
    (b.lastSeenAt ?? '').localeCompare(a.lastSeenAt ?? ''),
  );
  const veraltet = stationen.filter((s) => !zuletzt(s.lastSeenAt).frisch).length;

  return (
    <LeitstandRahmen
      titel="Stationen"
      benutzer={user?.displayName}
      werkzeuge={
        <button
          onClick={() => navigate('/einrichten/geraet-koppeln')}
          className="rounded-[4px] border border-akzent bg-akzent px-3 py-1.5 font-zahl text-xs text-akzent-satz"
        >
          Gerät koppeln
        </button>
      }
    >
      <div className="flex flex-col gap-6">
        <Link to="/einrichten" className="text-[13px] text-tinte-3 hover:text-tinte-2">
          ← Katalog
        </Link>

        {fehler && (
          <Notiz titel="Konnte nicht laden">
            {fehler}{' '}
            <button onClick={() => void lade()} className="underline">
              Erneut versuchen
            </button>
          </Notiz>
        )}

        {laden && stationen.length === 0 && !fehler && (
          <p className="py-12 text-center text-sm text-tinte-3">Lade Stationen …</p>
        )}

        {!laden && !fehler && (
          <>
            {veraltet > 0 && (
              <Notiz titel="Altbestand erkannt">
                {veraltet} {veraltet === 1 ? 'Station wurde' : 'Stationen wurden'} seit über zwei
                Wochen nicht gesehen — vermutlich Test-Reste und alte Kopplungen. Sie werden beim
                Cutover bereinigt (Z14); real im Einsatz sind die WSA- und die Vorarbeiter-Station.
              </Notiz>
            )}

            <Karte
              titel="Stationen"
              marke={`${stationen.length} Einträge — sortiert nach zuletzt gesehen`}
              tabelle
            >
              <div className="overflow-x-auto">
                <Tabelle>
                  <thead>
                    <tr>
                      <Kopfzelle>Name</Kopfzelle>
                      <Kopfzelle>Typ</Kopfzelle>
                      <Kopfzelle>Standort</Kopfzelle>
                      <Kopfzelle>Zustand</Kopfzelle>
                      <Kopfzelle rechts>Zuletzt gesehen</Kopfzelle>
                    </tr>
                  </thead>
                  <tbody>
                    {sortiert.map((s, i) => {
                      const letzte = i === sortiert.length - 1;
                      const gesehen = zuletzt(s.lastSeenAt);
                      return (
                        <tr
                          key={s.id}
                          onClick={() => navigate(`/einrichten/stationen/${s.id}`)}
                          className={`cursor-pointer hover:bg-flaeche-2 ${s.isActive && gesehen.frisch ? '' : 'opacity-60'}`}
                        >
                          <Zelle letzte={letzte}>
                            {s.name || <span className="text-tinte-3">ohne Namen</span>}
                          </Zelle>
                          <Zelle letzte={letzte}>
                            <Kennung>{TYP_KURZ[s.type] ?? s.type}</Kennung>
                          </Zelle>
                          <Zelle letzte={letzte}>
                            {s.location || <span className="text-tinte-3">—</span>}
                          </Zelle>
                          <Zelle letzte={letzte}>
                            {s.isActive ? (
                              <Zustand art="gut">aktiv</Zustand>
                            ) : (
                              <span className="font-zahl text-[11px] text-tinte-3">
                                außer Betrieb
                              </span>
                            )}
                          </Zelle>
                          <Zelle rechts zahl letzte={letzte}>
                            <span className={gesehen.frisch ? '' : 'text-tinte-3'}>
                              {gesehen.text}
                            </span>
                          </Zelle>
                        </tr>
                      );
                    })}
                  </tbody>
                </Tabelle>
              </div>
            </Karte>

            <AbmeldeZeit />
          </>
        )}
      </div>
    </LeitstandRahmen>
  );
}
